/*
 * SPDX-FileCopyrightText: 2023 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ErrorModel,
  GlobalMessageService,
  HttpErrorHandler,
  HttpResponseStatus,
  Priority,
} from '@spartacus/core';
import {
  isVoucherError,
  voucherNotQualifyError,
  voucherExpiredError,
  voucherExceededError,
  voucherInvalidError,
  voucherAlreadyExistError,
} from '../../utils/utils';
import { GenCartCouponService } from 'src/app/generac/services/cart-coupon.service';

@Injectable({
  providedIn: 'root',
})
export class BadVoucherRequestHandler extends HttpErrorHandler {
  override responseStatus = HttpResponseStatus.BAD_REQUEST;

  constructor(
    globalMessageService: GlobalMessageService,
    public cartCouponService: GenCartCouponService,
  ) {
    super(globalMessageService);
  };

  getPriority(): Priority {
    return Priority.NORMAL;
  }

  override hasMatch(errorResponse: HttpErrorResponse): boolean {
    return (
      super.hasMatch(errorResponse) &&
      this.getErrors(errorResponse).some(isVoucherError)
    );
  }

  handleError(request: HttpRequest<any>, response: HttpErrorResponse): void {
    this.handleVoucherExceededError(request, response);
    this.handleVoucherExpiredError(request, response);
    this.handleVoucherNotQualifyError(request, response);
    this.handleVoucherInvalidError(request, response);
    this.handleVoucherAlreadyExistError(request, response);
    this.handleVoucherHttpError(request, response);
  }

  protected handleVoucherExceededError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse
  ): void {
    this.getErrors(response)
      .filter((e) => voucherExceededError(e))
      .forEach(() => {
        this.cartCouponService.addError({key: 'httpHandlers.voucherExceeded'});
      });
  }

  protected handleVoucherExpiredError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse
  ): void {
    this.getErrors(response)
      .filter((e) => voucherExpiredError(e))
      .forEach(() => {
        this.cartCouponService.addError({key: 'httpHandlers.voucherExpiredError'});
      });
  }

  protected handleVoucherNotQualifyError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse
  ): void {
    this.getErrors(response)
      .filter((e) => voucherNotQualifyError(e))
      .forEach(() => {
        this.cartCouponService.addError({key: 'httpHandlers.voucherNotQualifyError'});
      });
  }

  protected handleVoucherInvalidError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse
  ): void {
    this.getErrors(response)
      .filter((e) => voucherInvalidError(e))
      .forEach(() => {
        this.cartCouponService.addError({key: 'httpHandlers.invalidCodeProvided'});
      });
  }

  protected handleVoucherAlreadyExistError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse
  ): void {
    this.getErrors(response)
      .filter((e) => voucherAlreadyExistError(e))
      .forEach(() => {
        this.cartCouponService.addError({key: 'httpHandlers.voucherAlreadyExist'});
      });
  }

  protected handleVoucherHttpError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse
  ): void {
    this.getErrors(response)
      .filter((e) => !voucherExpiredError(e) && !voucherNotQualifyError(e) && !voucherInvalidError(e)
        && !voucherExceededError(e) && !voucherAlreadyExistError(e))
      .forEach((e) => {
        this.cartCouponService.addError(e.message ? e.message : {key: 'httpHandlers.unknownError'});
      });
  }

  protected getErrors(response: HttpErrorResponse): ErrorModel[] {
    return (response.error?.errors || []).filter(
      (error: ErrorModel) => error.type !== 'JaloObjectNoLongerValidError'
    );
  }
}
